export class Utils {

  private studentcenterUrls: string[] = [
    '/studentcenter',
    '/navigator',
    '/onlineexams',
    '/account',
    '/cart',
    '/privatetutoring',
    '/challengeproblems'
  ]
  private isStudentCenterUrlRegExp: RegExp = new RegExp(this.studentcenterUrls.join('|'))

  public constructor(private _window: Window, private _inflection) {}

  public singularize(input: string) {
    return this._inflection.singularize(input)
  }

  public isPlural(input: string) {
    return input && input.length > 1 && input.endsWith('s')
  }

  public capitalizeFirst(input: string) {
    return input.substring(0, 1).toUpperCase() + input.substring(1)
  }

  public camelCase(input: string) {
    return input.replace(/[-_ ](.)/g, (match, group1) => {
        return group1.toUpperCase()
    })
  }

  public pascalCase(input: string) {
    return this.singularize(this.capitalizeFirst(this.camelCase(input)))
  }

  public dashCase(input: string) {
    return input.replace(/([a-z])([A-Z])/g, '$1-$2').replace(/([^-])[^a-z]+/gi, '$1-').toLowerCase().trim()
  }

  public snakeCase(input: string) {
    return input.replace(/([a-z])([A-Z])/g, '$1_$2').replace(/([^-])[^a-z]+/gi, '$1_').toLowerCase().trim()
  }

  public propertyPresent(obj: object, ...listOfProperties: Array<string|number>) {
    return this.evaluateCallbackOnProperties(obj, this.propertyPresentCallback, listOfProperties)
  }

  public propertyNotEmpty(obj: object, ...listOfProperties: Array<string|number>) {
    return this.evaluateCallbackOnProperties(obj, this.propertyNotEmptyCallback, listOfProperties)
  }

  public mapSet<T, R>(
    set: Set<T>,
    mapFn: (currentValue: T, currentIndex?: T, set?: Set<T>) => R
  ): R[] {
    const result = []
    set.forEach((value, i, s) => result.push(mapFn(value, i, s)))
    return result
  }

  public reduceSet<T>(
    set: Set<T>,
    reduceFn: (previousValue: T, currentValue: T, currentIndex?: T, set?: Set<T>) => any,
    initialValue?: any
  ): any {
    const setValueIterator = set.values()
    let result = initialValue
    let setValue = setValueIterator.next()
    if (initialValue === undefined) {
      if (setValue.done) {
        throw new Error('Cannot reduce empty set with no initial value')
      }
      result = setValue.value
      setValue = setValueIterator.next()
    }

    while (!setValue.done) {
      result = reduceFn(result, setValue.value, setValue.value, set)
      setValue = setValueIterator.next()
    }

    return result
  }

  public findSet<T>(set: Set<T>, findFn: (currentValue: T, currentIndex?: T, set?: Set<T>) => boolean): T {
    let result: T
    set.forEach((value, i, s) => {
      if (result) {
        return
      } else if (findFn(value, i, s)) {
        result = value
      }
    })
    return result
  }

  public parseBoolean(input: string | number) {
    const comparedValue = String(input).toString().toLowerCase()
    let result
    switch (comparedValue) {
      case 'true': case '1': case 'on': case 'yes':
        result = true
        break
      case 'false': case '0': case 'off': case 'no':
      default:
        result = false
        break
    }
    return result
  }

  public getSortedObject(obj: object) {
    return (typeof obj !== 'object' || !obj) ? obj :
      Object.keys(obj).sort().reduce((sortedObj, key) => {
        const value = obj[key]
        if (typeof value === 'object' && Object.keys(value).some((subKey) => value[subKey] === obj) ) {
          return sortedObj
        }
        sortedObj[key] = this.getSortedObject(obj[key])
        return sortedObj
      }, {})
  }

  public hashCode(str: string) {
    let hash = 0
    for (let i = 0; i < str.length; i++) {
      /* tslint:disable:no-bitwise */
      hash = Math.floor(((hash << 5) - hash) + str.charCodeAt(i))
      /* tslint:enable:no-bitwise */
    }
    return hash
  }

  public arrayEquals(arr1: string[], arr2: string[]) {
    return JSON.stringify(arr1.sort()) === JSON.stringify(arr2.sort())
  }

  /**
   * @deprecated Use ObjectHelper#clone() instead
   */
  public cloneObject<T>(
    type: {new(): T},
    instance: T,
    defaultOptions: {include: string[],
    shallow?: string[]},
    options?
  ): T {
    const opts = options || {}
    opts.exclude = Array.isArray(opts.exclude) ? opts.exclude : []
    opts.include = Array.isArray(opts.include) ? opts.include : defaultOptions.include
    opts.shallow = Array.isArray(opts.shallow) ? opts.shallow : defaultOptions.shallow || []

    opts.shallow = opts.shallow.filter((shallowAttribute) => ! opts.exclude.includes(shallowAttribute))
    opts.include = opts.include.filter((includedAttribute) => ! opts.exclude.includes(includedAttribute)
      && ! opts.shallow.includes(includedAttribute))

    const cloneValue = (value) => typeof value['clone'] === 'function' ? value.clone() : value
    const result = new type()

    let propertyCopy
    opts.include.forEach((propertyName) => {
      if (!(propertyName in instance)) { return }

      const propertyValueToCopy = instance[propertyName]
      if (Array.isArray(propertyValueToCopy)) {
        propertyCopy = propertyValueToCopy.map((arrayValue) => cloneValue(arrayValue))
      } else if (propertyValueToCopy instanceof Date) {
        propertyCopy = new Date(+propertyValueToCopy)
      } else if (typeof propertyValueToCopy === 'object' && propertyValueToCopy !== null) {
        propertyCopy = cloneValue(propertyValueToCopy)
      } else {
        propertyCopy = propertyValueToCopy
      }
      result[propertyName] = propertyCopy
    })
    opts.shallow.forEach((propertyName) => {
      if (!(propertyName in instance)) { return }
      const propertyValueToCopy = instance[propertyName]
      if (Array.isArray(propertyValueToCopy)) {
        result[propertyName] = this.arrayCopy(propertyValueToCopy)
      } else {
        result[propertyName] = propertyValueToCopy
      }
    })

    return result
  }

  public arrayCopy<T>(arr: T[]): T[] {
    const objectToCopy = Array.isArray(arr) ? arr : []
    return [].concat(objectToCopy)
  }

  public isSubsetOf(superset: string[], subset: string[]): boolean {
    return subset.every((val) => {
      const countInSubset = subset.filter((el) => el === val).length
      const countInSuperset = superset.filter((el) => el === val).length
      return countInSubset <= countInSuperset
    })
  }

  public isStudentCenterUrl(url: string): boolean {
    return this.isStudentCenterUrlRegExp.test(url.toLowerCase())
  }

  public downloadUrl(url: string): void {
    return this.clickBlankLink(url, (linkEl: HTMLAnchorElement) => {
      linkEl.download = url
    })
  }

  public openInWindow(url: string): void {
    return this.clickBlankLink(url)
  }

  public filterKeys(object: {}, filterSpec: {type: string, keys: string[]}) {
    const filterStrategies = {
      include: (keys, key) => keys.includes(key),
      exclude: (keys, key) => ! keys.includes(key)
    }
    return Object.keys(object)
      .filter((key) => filterStrategies[filterSpec.type](filterSpec.keys, key))
      .reduce((obj, key) => {
        obj[key] = object[key]
        return obj
      }, {})
  }

  public isValueEmpty(value: any): boolean {
    let result = true
    switch (typeof value) {
      case 'undefined':
        result = true
        break
      case 'boolean':
      case 'number':
        result = false
        break
      case 'string':
        result = value.trim() === ''
        break
      case 'object':
        result = value === null || Object.keys(value).length === 0
        break
      default:
        if (Array.isArray(value) && value.length <= 0) {
          result = true
        }
    }

    return result
  }

  public getKeyByValue(obj: object, value: any) {
    const foundEntry = Object.entries(obj).find((entry) => entry[1] === value)
    return foundEntry && foundEntry[0]
  }

  private evaluateCallbackOnProperties(obj: object, callback, listOfProperties: Array<string|number>) {
    return !listOfProperties.some((propertyName) => {
      if (callback.call(this, obj, propertyName) === false) {
        return true
      }
      obj = obj[propertyName]
    })
  }

  private clickBlankLink(url: string, linkModifier: (linkEl: HTMLAnchorElement) => void = null): void {
    const document = this._window.document
    const visibleElContainer = document.getElementsByTagName('body')[0]

    const linkEl: HTMLAnchorElement = document.createElement('a')
    linkEl.href = url
    linkEl.target = '_blank'
    linkEl.style.display = 'none'

    if (linkModifier instanceof Function) {
      linkModifier.call(linkEl, linkEl)
    }

    // Element needs to be appended to DOM, otherwise some browsers ignore the click (as this should be impossible)
    visibleElContainer.appendChild(linkEl)
    linkEl.click()
    visibleElContainer.removeChild(linkEl)
  }

  private propertyPresentCallback(obj, propertyName) {
    if (
      typeof obj === 'undefined' ||
      obj === null ||
      typeof obj[propertyName] === 'undefined' ||
      obj[propertyName] === null
    ) {
      return false
    }
  }

  private propertyNotEmptyCallback(obj, propertyName): boolean {
    const value = obj[propertyName]
    return ! this.isValueEmpty(value)
  }
}



// WEBPACK FOOTER //
// ./node_modules/@mprep/shared/lib/helpers/utils/utils.ts